import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Trophy, Clock, MessageSquare, Star, Award, RefreshCw, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { v: "today", l: "Today" },
  { v: "week", l: "This Week" },
  { v: "month", l: "This Month" },
  { v: "all", l: "All Time" },
];

function fmtSecs(s) {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m - h * 60}m`;
}

export default function LiveSupportPerformance() {
  const [data, setData] = useState({ totals: {}, agents: [], leaderboard: [] });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [ratings, setRatings] = useState([]);
  const [showRatings, setShowRatings] = useState(false);
  const [sortKey, setSortKey] = useState("score");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/live-support/performance?period=${period}`);
      setData(data || { totals: {}, agents: [], leaderboard: [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const loadRatings = async () => {
    try {
      const { data } = await api.get("/admin/live-support/ratings?limit=50");
      setRatings(data?.items || []);
      setShowRatings(true);
    } catch (e) { toast.error("Could not load ratings"); }
  };

  const sortedAgents = [...(data.agents || [])].sort((a, b) => {
    const get = (x) => x[sortKey] ?? -1;
    return (get(b) || 0) - (get(a) || 0);
  });

  const { totals = {}, leaderboard = [] } = data;

  return (
    <div data-testid="ls-performance-page">
      <PageHeader title="Live Support — Agent Performance" subtitle="Track every agent's response time, rating, handled chats, and weekly leaderboard." />

      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`ls-perf-${p.v}`}
                    className={`h-9 px-3 rounded-sm text-xs font-bold border ${period === p.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
              {p.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadRatings} className="h-9 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 inline-flex items-center gap-1">
            <Star size={13} /> View Ratings
          </button>
          <button onClick={load} disabled={loading} className="h-9 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 inline-flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Top stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard label="Agents" value={`${totals.agents || 0}/${totals.agents_total || 0}`} icon={Users} color="text-blue-600 bg-blue-50" />
        <StatCard label="Sessions" value={totals.sessions || 0} icon={MessageSquare} color="text-purple-600 bg-purple-50" />
        <StatCard label="Open Now" value={totals.open_now || 0} icon={Clock} color="text-amber-600 bg-amber-50" />
        <StatCard label="Active Now" value={totals.active_now || 0} icon={TrendingUp} color="text-emerald-600 bg-emerald-50" />
        <StatCard label="Ratings" value={totals.ratings || 0} icon={Star} color="text-yellow-600 bg-yellow-50" />
        <StatCard label="Avg Rating" value={totals.rating_avg !== null && totals.rating_avg !== undefined ? `${totals.rating_avg} ⭐` : "—"} icon={Award} color="text-orange-600 bg-orange-50" />
      </div>

      {/* Leaderboard podium */}
      {leaderboard.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 via-white to-purple-50 border border-amber-200 rounded-sm p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={18} className="text-amber-500" />
            <div className="font-extrabold text-gray-900">🏆 Leaderboard</div>
            <span className="text-xs text-gray-500">— Composite score (response speed 30% · rating 40% · volume 30%)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {leaderboard.slice(0, 3).map((a, i) => (
              <div key={a.agent_id} className={`rounded-sm p-3 border-2 ${i === 0 ? "border-amber-300 bg-amber-50" : i === 1 ? "border-slate-300 bg-slate-50" : "border-orange-300 bg-orange-50"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-2xl">{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 truncate">{a.name}</div>
                    <div className="text-[10px] font-mono text-gray-500 truncate">{a.phone}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-extrabold text-gray-900">{a.score}</div>
                    <div className="text-[9px] text-gray-500 uppercase tracking-wider">score</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1 text-center">
                  <div className="bg-white rounded p-1.5">
                    <div className="text-[10px] text-gray-500">handled</div>
                    <div className="font-bold text-gray-900">{a.handled}</div>
                  </div>
                  <div className="bg-white rounded p-1.5">
                    <div className="text-[10px] text-gray-500">resp</div>
                    <div className="font-bold text-gray-900">{fmtSecs(a.avg_response_secs)}</div>
                  </div>
                  <div className="bg-white rounded p-1.5">
                    <div className="text-[10px] text-gray-500">rating</div>
                    <div className="font-bold text-gray-900">{a.rating_avg ?? "—"} {a.rating_avg && "⭐"}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All agents table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-600 font-bold">
                <th className="px-3 py-2.5">Agent</th>
                <Th k="handled" sortKey={sortKey} setSortKey={setSortKey}>Handled</Th>
                <Th k="active_now" sortKey={sortKey} setSortKey={setSortKey}>Active</Th>
                <Th k="closed" sortKey={sortKey} setSortKey={setSortKey}>Closed</Th>
                <th className="px-3 py-2.5">Avg Response</th>
                <th className="px-3 py-2.5">Avg Resolution</th>
                <Th k="rating_avg" sortKey={sortKey} setSortKey={setSortKey}>Rating</Th>
                <th className="px-3 py-2.5">Submissions</th>
                <Th k="score" sortKey={sortKey} setSortKey={setSortKey}>Score</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="p-8 text-center text-gray-500">Loading...</td></tr>
               : sortedAgents.length === 0 ? <tr><td colSpan={9} className="p-8 text-center text-gray-500">No agents yet</td></tr>
               : sortedAgents.map((a) => (
                <tr key={a.agent_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-emerald-500 text-white font-bold text-xs flex items-center justify-center">
                        {(a.name || "U").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{a.name}</div>
                        <div className="text-[10px] font-mono text-gray-500">{a.phone}</div>
                      </div>
                      {!a.active && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-bold">BLOCKED</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-bold text-gray-900">{a.handled}</td>
                  <td className="px-3 py-2.5 text-emerald-700 font-bold">{a.active_now}</td>
                  <td className="px-3 py-2.5 text-gray-600">{a.closed}</td>
                  <td className="px-3 py-2.5 text-gray-900">{fmtSecs(a.avg_response_secs)}</td>
                  <td className="px-3 py-2.5 text-gray-700">{fmtSecs(a.avg_resolution_secs)}</td>
                  <td className="px-3 py-2.5">
                    {a.rating_avg !== null ? (
                      <div className="inline-flex items-center gap-1">
                        <span className="font-bold text-gray-900">{a.rating_avg}</span>
                        <span className="text-amber-400">⭐</span>
                        <span className="text-[10px] text-gray-500">({a.rating_count})</span>
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className="text-emerald-700 font-bold">✓{a.sub_resolved}</span>
                    {" / "}
                    <span className="text-red-600 font-bold">✕{a.sub_rejected}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    {a.score !== null ? (
                      <div className="inline-flex items-center">
                        <ScoreBar score={a.score} />
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showRatings && (
        <RatingsModal items={ratings} onClose={() => setShowRatings(false)} />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`h-7 w-7 rounded ${color.split(" ")[1] || ""} flex items-center justify-center`}>
          <Icon size={14} className={color.split(" ")[0] || ""} />
        </div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
      </div>
      <div className="text-xl font-extrabold text-gray-900">{value}</div>
    </div>
  );
}

function Th({ k, sortKey, setSortKey, children }) {
  return (
    <th onClick={() => setSortKey(k)} className="px-3 py-2.5 cursor-pointer hover:text-emerald-700 select-none">
      {children} {sortKey === k && <span className="text-emerald-600">↓</span>}
    </th>
  );
}

function ScoreBar({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="font-bold text-gray-900 text-xs w-9 text-right">{score}</div>
    </div>
  );
}

function RatingsModal({ items, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="font-bold text-gray-900">Recent Customer Ratings ({items.length})</div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">✕</button>
        </div>
        <div className="overflow-y-auto p-3">
          {items.length === 0 && <div className="p-10 text-center text-gray-500 text-sm">No ratings yet — they appear here when users submit feedback after a chat ends.</div>}
          {items.map((r) => (
            <div key={r.id} className="border-b border-gray-100 last:border-0 py-3 px-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => <span key={i} className={`text-base ${i <= r.score ? "text-amber-400" : "text-gray-300"}`}>★</span>)}
                </div>
                <div className="text-xs text-gray-500">@{r.username} · ID {r.player_id || "—"}</div>
                <div className="ml-auto text-[11px] text-gray-400">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="text-[11px] text-emerald-700 font-semibold mb-1">→ {r.agent_name}</div>
              {r.comment && <div className="text-sm text-gray-700 italic">&ldquo;{r.comment}&rdquo;</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
